import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'lucide-react';
import AllergenIcon from './AllergenIcon.jsx';

// Allergen catalog (same as backend)
const ALLERGEN_CATALOG = [
  {
    "id": "wheat",
    "icon": "wheat"
  },
  {
    "id": "fish",
    "icon": "fish"
  },
  {
    "id": "peanuts",
    "icon": "peanut"
  },
  {
    "id": "tree_nuts",
    "icon": "tree-nut"
  },
  {
    "id": "milk",
    "icon": "milk"
  },
  {
    "id": "celery",
    "icon": "celery"
  },
  {
    "id": "crustaceans",
    "icon": "shrimp"
  },
  {
    "id": "eggs",
    "icon": "egg"
  },
  {
    "id": "mustard",
    "icon": "mustard"
  },
  {
    "id": "sesame",
    "icon": "sesame"
  },
  {
    "id": "mollusks",
    "icon": "shell"
  },
  {
    "id": "lupin",
    "icon": "lupin"
  },
  {
    "id": "soybeans",
    "icon": "soy"
  },
  {
    "id": "sulfites",
    "icon": "sulfite"
  }
];

const AllergenMultiSelect = ({ 
  selectedAllergens = [], 
  onChange, 
  placeholder = "Select allergens...",
  className = "",
  disabled = false,
  openUpward = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter allergens based on search term
  const filteredAllergens = ALLERGEN_CATALOG.filter(allergen =>
    t(`allergens.${allergen.id}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
    allergen.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleAllergen = (allergenId) => {
    const newSelection = selectedAllergens.includes(allergenId)
      ? selectedAllergens.filter(id => id !== allergenId)
      : [...selectedAllergens, allergenId];
    
    onChange(newSelection);
  };

  const handleRemoveAllergen = (allergenId) => {
    const newSelection = selectedAllergens.filter(id => id !== allergenId);
    onChange(newSelection);
  };

  const getAllergenById = (id) => {
    return ALLERGEN_CATALOG.find(allergen => allergen.id === id);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Allergens Display */}
      <div 
        className={`min-h-[42px] border border-border rounded-lg bg-background p-2 flex flex-wrap gap-2 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedAllergens.length > 0 ? (
          selectedAllergens.map(allergenId => {
            const allergen = getAllergenById(allergenId);
            return (
              <div
                key={allergenId}
                className="inline-flex items-center gap-1 bg-destructive/10 text-destructive px-2 py-1 rounded-md text-sm"
              >
                <AllergenIcon allergenId={allergenId} size="xs" />
                <span className="text-xs font-medium">{allergen?.label}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAllergen(allergenId)}
                    className="text-destructive hover:text-destructive/80 ml-1"
                    title="Remove allergen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <span className="text-muted-foreground text-sm py-1">{placeholder}</span>
        )}
      </div>

      {/* Dropdown Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          !disabled && setIsOpen(!isOpen);
        }}
        disabled={disabled}
        className={`absolute right-2 top-2 p-1 text-muted-foreground hover:text-foreground transition-colors ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? (openUpward ? 'rotate-0' : 'rotate-180') : (openUpward ? 'rotate-180' : 'rotate-0')}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute z-50 w-full bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-hidden ${
          openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}>
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search allergens..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
              />
            </div>
          </div>

          {/* Allergen Options */}
          <div className="max-h-48 overflow-y-auto">
            {filteredAllergens.length > 0 ? (
              filteredAllergens.map(allergen => (
                <button
                  key={allergen.id}
                  type="button"
                  onClick={() => handleToggleAllergen(allergen.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                    selectedAllergens.includes(allergen.id) 
                      ? 'bg-primary/20 text-primary' 
                      : 'text-card-foreground'
                  }`}
                >
                  <AllergenIcon allergenId={allergen.id} size="sm" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{t(`allergens.${allergen.id}`)}</div>
                    <div className="text-xs text-muted-foreground truncate">{t(`allergens.${allergen.id}.examples`)}</div>
                  </div>
                  {selectedAllergens.includes(allergen.id) && (
                    <div className="text-primary">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                {t('common.noAllergensFound', { searchTerm })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AllergenMultiSelect;
